const selectors = {
	root: '[data-blog-filters]',
	results: '[data-blog-results]',
	status: '[data-blog-status]',
	aside: '.p-archive-blog__aside',
	form: '[data-filters-form]',
	clear: '[data-filters-clear]',
	apply: '[data-filters-apply]',
	toggle: '[data-filters-toggle]',
	summary: '[data-filters-summary]',
	selectedCount: '[data-selected-count]',
	chipRemove: '[data-chip-remove]',
};

/**
 * Splits comma-separated slugs into an array.
 *
 * @param {string} value Comma-separated slugs.
 * @return {string[]} Sanitized slug list.
 */
const toSlugArray = ( value ) => {

	if ( !value || typeof value !== 'string' ) {

		return [];

	}

	return value
		.split( ',' )
		.map( ( slug ) => slug.trim() )
		.filter( Boolean );

};

/**
 * Extracts page number from pretty permalink path.
 *
 * @param {string} pathname Pathname to parse.
 * @return {number} Page number (defaults to 1).
 */
const getPageFromPath = ( pathname ) => {

	const match = ( pathname || '' ).match( /\/page\/(\d+)\/?$/ );

	if ( !match ) {

		return 1;

	}

	const page = parseInt( match[ 1 ], 10 );
	return Number.isNaN( page ) ? 1 : page;

};

/**
 * Extracts page number from given URL (pretty permalink first, then legacy query).
 *
 * @param {string} href URL to parse.
 * @return {number} Page number (defaults to 1).
 */
const getPageFromLink = ( href ) => {

	try {

		const url = new URL( href, window.location.origin );

		const pathPage = getPageFromPath( url.pathname );
		if ( pathPage > 1 ) {

			return pathPage;

		}

		const paged = parseInt( url.searchParams.get( 'paged' ), 10 );
		if ( !Number.isNaN( paged ) && paged > 0 ) {

			return paged;

		}

		const page = parseInt( url.searchParams.get( 'page' ), 10 );
		return Number.isNaN( page ) ? 1 : page;

	} catch ( e ) {

		return 1;

	}

};

/**
 * Initializes archive filters UI and behaviors.
 */
const archiveFilters = () => {

	const filtersRoot = document.querySelector( selectors.root );
	const resultsContainer = document.querySelector( selectors.results );
	const archiveWrapper = document.querySelector( '[data-blog-archive]' );
	const aside = document.querySelector( selectors.aside );

	if ( !filtersRoot || !resultsContainer || !archiveWrapper || !aside ) {

		return;

	}

	const statusRegion = document.querySelector( selectors.status );

	const form = filtersRoot.querySelector( selectors.form );
	const inputs = Array.from( form?.querySelectorAll( 'input[type="checkbox"]' ) || [] );
	const toggleButtons = Array.from( document.querySelectorAll( selectors.toggle ) );
	const closeButtons = Array.from( document.querySelectorAll( '[data-filters-close]' ) );
	const clearButtons = Array.from( document.querySelectorAll( selectors.clear ) );
	const applyButtons = Array.from( document.querySelectorAll( selectors.apply ) );
	const summaryContainer = document.querySelector( selectors.summary );
	const selectedCountElements = Array.from(
		filtersRoot.querySelectorAll( selectors.selectedCount ),
	);

	const initialPage = getPageFromLink( window.location.href );

	const labels = inputs.reduce( ( map, input ) => {

		const labelText = input.closest( '.c-blog-filters__checkbox' )?.querySelector( '.c-blog-filters__label' )?.textContent?.trim();
		if ( labelText ) {

			map.set( input.value, labelText );

		}
		return map;

	}, new Map() );

	const state = {
		categories: toSlugArray( filtersRoot.dataset.initialCategories ),
		tags: toSlugArray( filtersRoot.dataset.initialTags ),
		page: initialPage,
	};

	const baseUrl = filtersRoot.dataset.baseUrl || archiveWrapper.dataset.baseUrl || window.location.pathname;
	const useTrailingSlash = archiveWrapper.dataset.trailingSlash !== '0';
	const ajaxUrl = window?.themeVars?.ajaxUrl || '/wp-admin/admin-ajax.php';
	const security = window?.themeVars?.security || '';

	let isLoading = false;
	let queuedPage = null;

	/**
	 * Builds readable query string for current filter state.
	 *
	 * @param {{categories: string[], tags: string[]}} filterState Current filters.
	 * @return {string} Query string without leading question mark.
	 */
	const buildQueryString = ( filterState ) => {

		const query = [];

		if ( filterState.categories.length ) {

			query.push( `categories=${filterState.categories.map( encodeURIComponent ).join( ',' )}` );

		}

		if ( filterState.tags.length ) {

			query.push( `tags=${filterState.tags.map( encodeURIComponent ).join( ',' )}` );

		}

		return query.join( '&' );

	};

	/**
	 * Builds shareable archive URL, compatible with /page/N pretty permalinks
	 * (with a trailing slash only if the permalink structure uses one).
	 *
	 * @param {number} page Page number.
	 * @return {string} Shareable URL.
	 */
	const buildArchiveUrl = ( page ) => {

		const url = new URL( baseUrl || '/', window.location.origin );

		// Ensure base path doesn't include paging or a trailing slash.
		const cleanedPath = url.pathname.replace( /\/page\/\d+\/?$/, '' ).replace( /\/+$/, '' );
		const path = page > 1 ? `${cleanedPath}/page/${page}` : cleanedPath;

		url.pathname = useTrailingSlash || !path ? `${path}/` : path;

		const queryString = buildQueryString( state );
		url.search = queryString ? `?${queryString}` : '';

		return `${url.pathname}${url.search}`;

	};

	/**
	 * Updates selected counter in UI.
	 */
	const updateCount = () => {

		const total = state.categories.length + state.tags.length;
		selectedCountElements.forEach( ( element ) => {

			element.textContent = `(${total})`;

		} );
		clearButtons.forEach( ( button ) => {

			button.classList.toggle( 'is-hidden', total === 0 );

		} );

	};

	/**
	 * Renders selected filter chips for mobile summary.
	 */
	const renderSummary = () => {

		if ( !summaryContainer ) {

			return;

		}

		summaryContainer.innerHTML = '';
		const chips = [
			...state.categories.map( ( slug ) => ( { slug, type: 'category' } ) ),
			...state.tags.map( ( slug ) => ( { slug, type: 'tag' } ) ),
		];

		chips.forEach( ( { slug, type } ) => {

			const chip = document.createElement( 'span' );
			chip.className = 'c-blog-filters-mobile__chip';
			chip.dataset.filterChip = '';
			chip.dataset.filterSlug = slug;
			chip.dataset.filterType = type;

			const label = document.createElement( 'span' );
			label.className = 'c-blog-filters-mobile__chip-label';
			label.textContent = labels.get( slug ) || slug;

			const remove = document.createElement( 'button' );
			remove.type = 'button';
			remove.className = 'c-blog-filters-mobile__chip-remove';
			remove.setAttribute( 'data-chip-remove', '' );
			remove.setAttribute( 'aria-label', 'Usuń filtr' );
			remove.innerHTML = `
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M17.9231 5L19 6.07692L13.076 12L19 17.9231L17.9231 19L12 13.076L6.07692 19L5 17.9231L10.924 12L5 6.07692L6.07692 5L12 10.924L17.9231 5Z" fill="#444444"/>
				</svg>
			`;

			chip.append( label, remove );
			summaryContainer.appendChild( chip );

		} );

	};

	/**
	 * Sets aria-expanded state for toggle buttons.
	 *
	 * @param {boolean} expanded Panel expanded flag.
	 */
	const setToggleState = ( expanded ) => {

		toggleButtons.forEach( ( button ) => {

			button.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );

		} );

	};

	/**
	 * Closes mobile filters panel.
	 */
	const closePanel = () => {

		aside.classList.remove( 'is-open' );
		setToggleState( false );
		document.documentElement.classList.remove( 'no-scroll' );

	};

	/**
	 * Opens mobile filters panel.
	 */
	const openPanel = () => {

		aside.classList.add( 'is-open' );
		setToggleState( true );
		document.documentElement.classList.add( 'no-scroll' );

	};

	/**
	 * Toggles mobile filters panel.
	 */
	const togglePanel = () => {

		if ( aside.classList.contains( 'is-open' ) ) {

			closePanel();

		} else {

			openPanel();

		}

	};

	/**
	 * Reads checkboxes and updates filter state.
	 */
	const updateStateFromInputs = () => {

		const categories = [];
		const tags = [];

		inputs.forEach( ( input ) => {

			if ( !input.checked ) {

				return;

			}

			if ( input.dataset.filterType === 'category' ) {

				categories.push( input.value );

			} else {

				tags.push( input.value );

			}

		} );

		state.categories = Array.from( new Set( categories ) );
		state.tags = Array.from( new Set( tags ) );

	};

	/**
	 * Syncs checkboxes with current filter state.
	 */
	const syncInputsWithState = () => {

		inputs.forEach( ( input ) => {

			if ( input.dataset.filterType === 'category' ) {

				input.checked = state.categories.includes( input.value );

			} else {

				input.checked = state.tags.includes( input.value );

			}

		} );

	};

	/**
	 * Builds history state object for the current filter state.
	 *
	 * @param {number} page Page number.
	 * @return {Object} History state.
	 */
	const buildHistoryState = ( page ) => ( {
		page,
		categories: [ ...state.categories ],
		tags: [ ...state.tags ],
	} );

	/**
	 * Replaces current history entry (used for filter changes).
	 *
	 * @param {number} page Page number.
	 */
	const replaceHistory = ( page ) => {

		window.history.replaceState( buildHistoryState( page ), '', buildArchiveUrl( page ) );

	};

	/**
	 * Pushes a new history entry (used for pagination navigation).
	 *
	 * @param {number} page Page number.
	 */
	const pushHistory = ( page ) => {

		window.history.pushState( buildHistoryState( page ), '', buildArchiveUrl( page ) );

	};

	/**
	 * @param {string} listing Listing HTML.
	 * @param {string} pagination Pagination HTML.
	 */
	const setResults = ( listing = '', pagination = '' ) => {

		resultsContainer.innerHTML = `${listing}${pagination}`;

	};

	/**
	 * Sends AJAX request to fetch filtered posts.
	 *
	 * @param {number}  page         Target page.
	 * @param {boolean} shouldScroll Whether to scroll results into view after update.
	 */
	const requestFilters = async ( page, shouldScroll = false ) => {

		if ( isLoading ) {

			queuedPage = page;
			return;

		}

		isLoading = true;
		resultsContainer.setAttribute( 'aria-busy', 'true' );

		const params = new URLSearchParams();
		params.set( 'action', 'wombat_blog_filter' );
		params.set( 'security', security );
		params.set( 'page', page );
		params.set( 'baseUrl', baseUrl );

		if ( state.categories.length ) {

			params.set( 'categories', state.categories.join( ',' ) );

		}

		if ( state.tags.length ) {

			params.set( 'tags', state.tags.join( ',' ) );

		}

		try {

			const response = await fetch( ajaxUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
				},
				body: params.toString(),
			} );

			const payload = await response.json();

			if ( payload?.success ) {

				const listing = payload?.data?.listing ?? '';
				const pagination = payload?.data?.pagination ?? '';

				setResults( listing, pagination );

				if ( shouldScroll ) {

					resultsContainer.scrollIntoView( { behavior: 'smooth', block: 'start' } );

				}

			} else if ( statusRegion ) {

				statusRegion.textContent = '';

			}

		} catch ( error ) {

			// eslint-disable-next-line no-console
			console.error( 'Blog filters request failed', error );

			if ( statusRegion ) {

				statusRegion.textContent = '';

			}

		} finally {

			isLoading = false;
			resultsContainer.removeAttribute( 'aria-busy' );

			if ( queuedPage !== null ) {

				const nextPage = queuedPage;
				queuedPage = null;
				void requestFilters( nextPage, shouldScroll );

			}

		}

	};

	/**
	 * Applies current filters and requests data.
	 *
	 * @param {number} page Target page.
	 */
	const applyFilters = ( page = 1 ) => {

		state.page = page;
		updateCount();
		renderSummary();
		replaceHistory( page );
		void requestFilters( page );

	};

	/**
	 * Handles checkbox changes.
	 */
	const handleInputChange = () => {

		updateStateFromInputs();
		applyFilters( 1 );

	};

	/**
	 * Clears all selected filters.
	 */
	const handleClear = () => {

		inputs.forEach( ( input ) => {

			input.checked = false;

		} );

		state.categories = [];
		state.tags = [];

		applyFilters( 1 );
		closePanel();

	};

	/**
	 * Handles chip removal click.
	 *
	 * @param {Event} event Click event.
	 */
	const handleChipRemove = ( event ) => {

		const chip = event.target.closest( '[data-filter-chip]' );

		if ( !chip ) {

			return;

		}

		const { filterSlug: slug, filterType: type } = chip.dataset;
		const relatedInput = inputs.find(
			( input ) => input.value === slug && input.dataset.filterType === type,
		);

		if ( relatedInput ) {

			relatedInput.checked = false;

		}

		updateStateFromInputs();
		applyFilters( 1 );

	};

	// Bind events.
	if ( form ) {

		form.addEventListener( 'submit', ( event ) => {

			event.preventDefault();

		} );

	}

	inputs.forEach( ( input ) => {

		input.addEventListener( 'change', handleInputChange );

	} );

	clearButtons.forEach( ( button ) => {

		button.addEventListener( 'click', handleClear );

	} );

	applyButtons.forEach( ( button ) => {

		button.addEventListener( 'click', () => {

			updateStateFromInputs();
			applyFilters( 1 );
			closePanel();

		} );

	} );

	toggleButtons.forEach( ( button ) => {

		button.addEventListener( 'click', togglePanel );

	} );

	closeButtons.forEach( ( button ) => {

		button.addEventListener( 'click', closePanel );

	} );

	if ( summaryContainer ) {

		summaryContainer.addEventListener( 'click', ( event ) => {

			if ( event.target.matches( selectors.chipRemove ) || event.target.closest( selectors.chipRemove ) ) {

				handleChipRemove( event );

			}

		} );

	}

	document.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Escape' ) {

			closePanel();

		}

	} );

	// Intercept pagination link clicks inside the results container.
	// Event delegation from the stable outer container handles AJAX-replaced pagination.
	resultsContainer.addEventListener( 'click', ( event ) => {

		const link = event.target.closest( 'a' );

		if ( !link || !link.closest( '.c-pagination__wrapper' ) ) {

			return;

		}

		event.preventDefault();

		const page = link.dataset.page
			? parseInt( link.dataset.page, 10 )
			: getPageFromLink( link.href );

		if ( !page || page < 1 ) {

			return;

		}

		state.page = page;
		pushHistory( page );
		void requestFilters( page, true );

	} );

	// Restore filter+page state on browser back/forward navigation.
	window.addEventListener( 'popstate', ( event ) => {

		const saved = event.state;

		if ( saved ) {

			state.categories = saved.categories || [];
			state.tags = saved.tags || [];
			state.page = saved.page || 1;

		} else {

			const params = new URLSearchParams( window.location.search );
			state.categories = toSlugArray( params.get( 'categories' ) || '' );
			state.tags = toSlugArray( params.get( 'tags' ) || '' );
			state.page = getPageFromPath( window.location.pathname );

		}

		syncInputsWithState();
		updateCount();
		renderSummary();
		void requestFilters( state.page );

	} );

	// Initial render.
	syncInputsWithState();
	updateCount();
	renderSummary();
	setToggleState( false );

	// Seed initial history state without changing the current URL.
	window.history.replaceState( buildHistoryState( state.page ), '', window.location.href );

};

document.addEventListener( 'DOMContentLoaded', archiveFilters );
